// Prompt 18 A3 — QuickBooks App Store Launch URL (/qbo/launch) and
// Connect/Reconnect URL (/qbo/connect) targets. The SPA handles
// logged-in vs logged-out routing so Intuit's static URLs don't need
// to know about session state.
//
// /qbo/launch:   logged out → /login?next=/connections ; logged in → /connections
// /qbo/connect:  logged out → /login?next=/qbo/connect ; logged in → start QBO
//                OAuth immediately (or render card if already connected)
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export function QboLaunch() {
  const { user } = useAuth();
  if (user === null) return <_Spinner />;
  if (!user) return <Navigate to="/login?next=/connections" replace />;
  return <Navigate to="/connections" replace />;
}

export function QboConnect() {
  const { user } = useAuth();
  const location = useLocation();
  const [state, setState] = useState("checking");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const s = await api.get("/qbo/status");
        if (s.data?.connected) {
          setState("already-connected");
          return;
        }
        const r = await api.get("/qbo/auth-url");
        if (r.data?.authorization_url) {
          window.location.href = r.data.authorization_url;
          return;
        }
        setError("Could not start QuickBooks authorization.");
        setState("error");
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not start QuickBooks authorization.");
        setState("error");
      }
    })();
  }, [user]);

  if (user === null) return <_Spinner />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (state === "already-connected") {
    return <Navigate to="/connections?qbo=already-connected" replace />;
  }
  if (state === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans"
           data-testid="qbo-connect-error">
        <div className="max-w-md card-flat p-8 text-center">
          <h1 className="font-display text-xl font-semibold">Couldn't start QuickBooks connect</h1>
          <p className="text-sm text-neutral-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  return <_Spinner label="Redirecting to QuickBooks…" />;
}

function _Spinner({ label = "Loading…" }) {
  return (
    <div className="min-h-screen flex items-center justify-center font-sans">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-neutral-400" size={32} />
        <p className="mt-3 text-sm text-neutral-500">{label}</p>
      </div>
    </div>
  );
}
